import { ChangeEvent, ReactElement, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import {
  OrganizationService,
  MembershipService,
  VALIDATION_RULES,
  getTaxIdExample,
  useApiGetter,
  useApiEffect,
} from '@localstack/services';

import { Box, Card, CardActions, CardContent, CardHeader, Grid, Switch, Typography } from '@mui/material';

import { ControlledCountryAutocomplete, ControlledTextField, ProgressButton, TaxIdTextField } from '@localstack/ui';

import { OrganizationType, Permission, PatchOrganizationRequest } from '@localstack/types';

import { useAuthProvider } from '~/hooks/useAuthProvider';
import { ContainedCustomerLayout } from '~/layouts';

export const WorkspaceSettings = (): ReactElement => {
  const { can } = useAuthProvider();

  const { data: organization } = useApiGetter(OrganizationService, 'getOrganization', []);

  const { patchOrganization, isLoading: isOrgMutating } = useApiEffect(OrganizationService, ['patchOrganization'], {
    revalidate: ['getOrganization'],
    revalidateOtherClient: {
      client: [MembershipService],
      methods: ['getPrimaryMembership'],
    },
  });

  const isOrgLoading = isOrgMutating;

  const orgForm = useForm<PatchOrganizationRequest>({ mode: 'all' });
  const { dirtyFields } = orgForm.formState;

  const country = orgForm.watch('billing_details.country');
  const taxId = orgForm.watch('tax_details.tax_id');

  const isCompany = organization?.basic_details.org_type === OrganizationType.COMPANY;

  const onSubmitOrgInfo = useCallback(
    async (data: PatchOrganizationRequest) => {
      if (organization && can(Permission.UPDATE_ORGANIZATION)) {
        await patchOrganization(data);
      }
    },
    [dirtyFields, organization, can],
  );

  // Set initial form values
  useEffect(() => {
    if (organization) {
      orgForm.reset({
        basic_details: {
          name: organization.basic_details.name,
          org_type: organization.basic_details.org_type,
        },
        billing_details: {
          country: organization.billing_details.country,
        },
        tax_details: {
          tax_id: organization.tax_details.tax_id,
          tax_id_type: organization.tax_details.tax_id_type,
        },
      });
    }
  }, [organization]);

  const orgToggleOnChange = (_: ChangeEvent, checked: boolean) => {
    patchOrganization({
      basic_details: {
        org_type: checked ? OrganizationType.COMPANY : OrganizationType.INDIVIDUAL,
      },
    });
  };

  return (
    <ContainedCustomerLayout title="Workspace">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {/* organzation info */}
          <form onSubmit={orgForm.handleSubmit(onSubmitOrgInfo)}>
            <Card style={{ marginTop: '1em' }}>
              <CardHeader title="Workspace Settings" />
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12}>
                    <ControlledTextField
                      control={orgForm.control}
                      name="basic_details.name"
                      label={isCompany ? 'Organization/Workspace Name' : 'Workspace Name'}
                      placeholder={isCompany ? 'Organization/Workspace Name' : 'Workspace Name'}
                      fullWidth
                      required
                      variant="outlined"
                      rules={VALIDATION_RULES.safeStringRequired}
                      disabled={isOrgLoading || !can(Permission.UPDATE_ORGANIZATION)}
                    />
                  </Grid>

                  {can(Permission.UPDATE_ORGANIZATION) && (
                    <>
                      {/* company toggle */}
                      <Grid xs={12} paddingX="8px">
                        <Box pt={3}>
                          <Switch
                            checked={isCompany}
                            onChange={orgToggleOnChange}
                            disabled={isOrgLoading}
                            color="primary"
                          />
                          <Typography variant="caption">Company Account</Typography>
                        </Box>
                      </Grid>

                      <Grid item md={6} xs={12}>
                        <ControlledCountryAutocomplete
                          control={orgForm.control}
                          name="billing_details.country"
                          rules={VALIDATION_RULES.required}
                          inputProps={{
                            label: 'Country',
                            placeholder: 'Country',
                            fullWidth: true,
                            required: true,
                            variant: 'outlined',
                            disabled: !isCompany,
                          }}
                        />
                      </Grid>
                      <Grid item md={6} xs={12}>
                        <TaxIdTextField
                          control={orgForm.control}
                          name="tax_details.tax_id"
                          label="TAX ID"
                          placeholder={getTaxIdExample(country) || ''}
                          fullWidth
                          variant="outlined"
                          rules={{
                            ...VALIDATION_RULES.safeStringOptional,
                            ...VALIDATION_RULES.taxId(country),
                          }}
                          taxId={taxId}
                          country={country}
                          onChangeTaxType={(newTaxType) =>
                            orgForm.setValue('tax_details.tax_id_type', newTaxType || undefined, { shouldDirty: true })
                          }
                          disabled={!isCompany}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </CardContent>
              <CardActions>
                <ProgressButton
                  color="primary"
                  variant="contained"
                  loading={isOrgLoading}
                  disabled={!can(Permission.UPDATE_ORGANIZATION)}
                  type="submit"
                >
                  Save Changes
                </ProgressButton>
              </CardActions>
            </Card>
          </form>
        </Grid>
      </Grid>
    </ContainedCustomerLayout>
  );
};
